import { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { ShoppingBag, Loader2, Package, AlertTriangle, CheckCircle, ArrowLeft, Tag } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StockBadge = ({ status }) => {
  if (status === "out_of_stock") return <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-500/20 text-red-400">Out of Stock</span>;
  if (status === "low_stock") return <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">Low Stock</span>;
  return <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400">In Stock</span>;
};

function ProductCard({ p, onBuy, buyLoading }) {
  const outOfStock = p.stock_status === "out_of_stock";
  return (
    <div className="bg-[#0F0F0F] border border-[#2A2A2A] rounded-2xl overflow-hidden hover:border-[#333] transition-all group" data-testid={`product-${p._id}`}>
      <Link to={`/product/${p._id}`} className="block">
        <div className="aspect-[4/3] bg-[#1A1A1A] flex items-center justify-center relative overflow-hidden">
          {p.image_url ? <img src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" /> : <Package className="w-16 h-16 text-[#2A2A2A]" />}
          <span className={`absolute top-3 left-3 text-[10px] font-semibold px-2.5 py-1 rounded-full capitalize ${p.category === "phone" ? "bg-[#D4AF37]/20 text-[#D4AF37]" : "bg-blue-500/20 text-blue-400"}`}>{p.category}</span>
          <div className="absolute top-3 right-3"><StockBadge status={p.stock_status} /></div>
          {outOfStock && <div className="absolute inset-0 bg-black/60 flex items-center justify-center"><span className="text-red-400 font-outfit font-bold text-lg">Out of Stock</span></div>}
        </div>
      </Link>
      <div className="p-5">
        <Link to={`/product/${p._id}`} className="hover:text-[#D4AF37] transition-colors">
          <h3 className="font-outfit text-lg font-bold text-[#F5F5F5] mb-1">{p.name}</h3>
        </Link>
        {p.description && <p className="text-[#A0A0A0] font-manrope text-sm mb-3 line-clamp-2">{p.description}</p>}
        <div className="flex items-center justify-between mt-4">
          <span className="font-outfit text-2xl font-bold text-[#D4AF37]">${p.price.toFixed(2)}</span>
        </div>
        <button onClick={() => onBuy(p._id)} disabled={outOfStock || buyLoading === p._id}
          className="w-full mt-4 bg-[#D4AF37] hover:bg-[#F0C541] text-black py-3 rounded-full font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-40 transition-all"
          data-testid={`buy-${p._id}`}>
          {buyLoading === p._id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ShoppingBag className="w-4 h-4" /> {outOfStock ? "Out of Stock" : "Buy Now"}</>}
        </button>
      </div>
    </div>
  );
}

export function ProductDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buyLoading, setBuyLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    axios.get(`${API}/store/products/${id}`).then(({ data }) => {
      setProduct(data); setLoading(false);
    }).catch(() => { setLoading(false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const showMsg = (text, type = "success") => { setMsg({ text, type }); setTimeout(() => setMsg(null), 5000); };

  const buy = async () => {
    if (!user) { navigate("/login"); return; }
    setBuyLoading(true);
    try {
      const { data } = await axios.post(`${API}/store/purchase`, { product_id: id }, { withCredentials: true });
      showMsg(data.message);
      setProduct(p => p ? { ...p, inventory_count: p.inventory_count - 1, stock_status: p.inventory_count - 1 <= 0 ? "out_of_stock" : p.inventory_count - 1 <= (p.reorder_threshold || 3) ? "low_stock" : "in_stock" } : p);
    } catch (err) { showMsg(err.response?.data?.detail || "Purchase failed.", "error"); }
    finally { setBuyLoading(false); }
  };

  if (loading) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" /></div>;
  if (!product) return <div className="min-h-screen bg-[#050505] px-6 py-16 text-center"><p className="text-[#A0A0A0]">Product not found.</p><Link to="/store" className="text-[#D4AF37] text-sm mt-4 inline-block">Back to Store</Link></div>;

  const outOfStock = product.stock_status === "out_of_stock";

  return (
    <div className="min-h-screen bg-[#050505] px-6 py-12">
      <div className="max-w-4xl mx-auto">
        <Link to="/store" className="text-[#D4AF37] text-sm font-manrope mb-6 inline-flex items-center gap-1 hover:underline"><ArrowLeft className="w-3 h-3" /> Back to Store</Link>

        {msg && (
          <div className={`mb-6 rounded-xl p-4 text-sm font-manrope flex items-center gap-2 ${msg.type === "error" ? "bg-red-500/10 border border-red-500/30 text-red-400" : "bg-emerald-500/10 border border-emerald-500/30 text-emerald-400"}`}>
            {msg.type === "error" ? <AlertTriangle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />} {msg.text}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-8" data-testid="product-detail">
          <div className="bg-[#1A1A1A] rounded-2xl overflow-hidden aspect-square flex items-center justify-center relative">
            {product.image_url ? <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" /> : <Package className="w-24 h-24 text-[#2A2A2A]" />}
            {outOfStock && <div className="absolute inset-0 bg-black/60 flex items-center justify-center"><span className="text-red-400 font-outfit font-bold text-2xl">Out of Stock</span></div>}
          </div>
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className={`text-[10px] font-semibold px-2.5 py-1 rounded-full capitalize ${product.category === "phone" ? "bg-[#D4AF37]/20 text-[#D4AF37]" : "bg-blue-500/20 text-blue-400"}`}>{product.category}</span>
              <StockBadge status={product.stock_status} />
            </div>
            <h1 className="font-outfit text-3xl font-bold text-[#F5F5F5] mb-3">{product.name}</h1>
            {product.description && <p className="text-[#A0A0A0] font-manrope text-base mb-6 leading-relaxed">{product.description}</p>}
            <div className="flex items-baseline gap-2 mb-8">
              <span className="font-outfit text-4xl font-bold text-[#D4AF37]">${product.price.toFixed(2)}</span>
              {product.sku && <span className="text-[#A0A0A0] text-xs font-mono flex items-center gap-1"><Tag className="w-3 h-3" /> {product.sku}</span>}
            </div>
            <button onClick={buy} disabled={outOfStock || buyLoading}
              className="w-full bg-[#D4AF37] hover:bg-[#F0C541] text-black py-4 rounded-full font-semibold text-lg flex items-center justify-center gap-2 disabled:opacity-40 transition-all"
              data-testid="product-buy-btn">
              {buyLoading ? <><Loader2 className="w-5 h-5 animate-spin" /> Processing...</> : <><ShoppingBag className="w-5 h-5" /> {outOfStock ? "Out of Stock" : "Buy Now"}</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Store() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buyLoading, setBuyLoading] = useState(null);
  const [msg, setMsg] = useState(null);
  const [category, setCategory] = useState("all");

  useEffect(() => {
    axios.get(`${API}/store/products`).then(({ data }) => {
      setProducts(data); setLoading(false);
    }).catch(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showMsg = (text, type = "success") => { setMsg({ text, type }); setTimeout(() => setMsg(null), 5000); };

  const buyProduct = async (productId) => {
    if (!user) { navigate("/login"); return; }
    setBuyLoading(productId);
    try {
      const { data } = await axios.post(`${API}/store/purchase`, { product_id: productId }, { withCredentials: true });
      showMsg(data.message);
      setProducts(prev => prev.map(p => {
        if (p._id !== productId) return p;
        const newInv = p.inventory_count - 1;
        return { ...p, inventory_count: newInv, stock_status: newInv <= 0 ? "out_of_stock" : newInv <= (p.reorder_threshold || 3) ? "low_stock" : "in_stock" };
      }));
    } catch (err) {
      showMsg(err.response?.data?.detail || "Purchase failed.", "error");
    } finally { setBuyLoading(null); }
  };

  const filtered = category === "all" ? products : products.filter(p => p.category === category);
  const categories = ["all", ...new Set(products.map(p => p.category))];

  if (loading) return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[#050505] px-6 py-12">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="font-outfit text-4xl sm:text-5xl font-bold text-[#F5F5F5] mb-3">Store</h1>
          <p className="text-[#A0A0A0] font-manrope text-base max-w-lg mx-auto">Phones and accessories for your Prestigious Wireless service.</p>
        </div>

        {/* Category filter */}
        <div className="flex justify-center gap-2 mb-8" data-testid="category-filter">
          {categories.map(c => (
            <button key={c} onClick={() => setCategory(c)} className={`px-4 py-2 rounded-full text-sm font-manrope font-medium transition-all capitalize ${category === c ? "bg-[#D4AF37] text-black" : "bg-[#1A1A1A] text-[#A0A0A0] border border-[#2A2A2A] hover:border-[#D4AF37]"}`}>{c}</button>
          ))}
        </div>

        {msg && (
          <div className={`max-w-2xl mx-auto mb-8 rounded-xl p-4 text-sm font-manrope flex items-center gap-2 ${msg.type === "error" ? "bg-red-500/10 border border-red-500/30 text-red-400" : "bg-emerald-500/10 border border-emerald-500/30 text-emerald-400"}`} data-testid="store-message">
            {msg.type === "error" ? <AlertTriangle className="w-4 h-4 flex-shrink-0" /> : <CheckCircle className="w-4 h-4 flex-shrink-0" />}
            {msg.text}
          </div>
        )}

        {filtered.length === 0 ? (
          <div className="text-center py-20">
            <Package className="w-12 h-12 text-[#A0A0A0] mx-auto mb-4" />
            <p className="text-[#A0A0A0] font-manrope">No products available{category !== "all" ? ` in ${category}` : ""}.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="product-grid">
            {filtered.map(p => <ProductCard key={p._id} p={p} onBuy={buyProduct} buyLoading={buyLoading} />)}
          </div>
        )}
      </div>
    </div>
  );
}
